import { useMemo } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType, number, object, ObjectSchema, string } from 'yup';

import { IntervalType, SetPriceWithProductPayload } from '@client/api';
import { yupResolver } from '@hookform/resolvers/yup';

import { IntervalOption, useIntervalOption } from '../../../hooks';
import { Button, FormField, FormSubmitButton, GridCol, GridRow, HelperText, Input, Label, Select } from '../../../ui';
import { getCurrencySymbol, isDefinedString } from '../../../utils';
import { CurrencyAdornment } from '../../currency-adornment';
import { CurrencyAutocomplete } from '../../currency-autocomplete';

const INTERVAL_OPTIONS: IntervalOption[] = ['Monthly', 'EveryThreeMonth', 'Yearly'];

const INTERVAL_TYPE_OPTIONS: IntervalType[] = ['Month', 'Year'];

type FormModel = Pick<SetPriceWithProductPayload, 'interval' | 'unitAmount'>;

const schema: ObjectSchema<FormModel> = object({
  unitAmount: object({
    amount: number().positive().required(),
    currency: object({
      code: string().required(),
    }).required(),
  }),
  interval: object({
    type: string<IntervalType>().trim().oneOf(['Month', 'Year']).required().label('IntervalType'),
    count: number().positive().required(),
  }).required(),
});

type FormValues = InferType<typeof schema>;
type FormPath = FieldPath<FormModel>;
type FormDefaultValues = DeepPartial<FormValues>;

export type ProductPriceEditPriceProps = {
  price: SetPriceWithProductPayload | undefined | null;
  onSubmit: (data: FormValues) => void;
  onCancel: () => void;
  defaultCurrencyCode?: string;
};

export const ProductPriceEditPrice = ({
  price,
  onSubmit,
  onCancel,
  defaultCurrencyCode,
}: ProductPriceEditPriceProps) => {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const { t: tValidation } = useTranslation('validation');

  const defaultUnitCurrencyCode = price?.unitAmount?.currency?.code ?? defaultCurrencyCode ?? '';

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      unitAmount: {
        amount: price?.unitAmount?.amount ?? ('' as any),
        currency: {
          code: defaultUnitCurrencyCode,
        },
      },
      interval: {
        type: price?.interval?.type ?? ('' as any),
        count: price?.interval?.count ?? ('' as any),
      },
    }),
    [defaultUnitCurrencyCode, price?.interval?.count, price?.interval?.type, price?.unitAmount?.amount],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { control, handleSubmit, setValue, formState } = methods;

  const { errors } = formState;

  const unitCurrencyCode = useWatch({
    control,
    name: `unitAmount.currency.code`,
    defaultValue: defaultUnitCurrencyCode,
  });

  const unitCurrencySymbol =
    isDefinedString(unitCurrencyCode) && isDefinedString(getCurrencySymbol(unitCurrencyCode))
      ? getCurrencySymbol(unitCurrencyCode)
      : undefined;

  const { intervalOption, updateIntervalOption } = useIntervalOption({
    interval: price?.interval ?? undefined,
    onUpdateIntervalOption: ({ intervalCount, intervalType }) => {
      setValue<FormPath>('interval.count', intervalCount || ('' as any), {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: true,
      });
      setValue<FormPath>('interval.type', intervalType || ('' as any), {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: true,
      });
    },
  });

  const intervalErrorMessage: string | undefined = useMemo(() => {
    if (errors?.interval == null) return;
    return tValidation('field.required');
  }, [errors?.interval, tValidation]);

  const hasIntervalErrorMessage = useMemo(
    () => !!intervalErrorMessage && intervalOption !== 'Custom',
    [intervalErrorMessage, intervalOption],
  );

  return (
    <FormProvider {...methods}>
      <div className="flex flex-1 flex-col items-start text-left">
        <GridRow cols={2} spacingX={1} spacingY={2}>
          {/** Pricing (price + currency) */}
          <GridCol span={1}>
            <Label size="small" className="mb-0.5 text-white" required>
              {tGlobal('price.unitAmount')}
            </Label>
            <FormField<FormValues, FormPath>
              name={'unitAmount.amount'}
              defaultValue={''}
              required
              render={({ field }) => (
                <CurrencyAdornment position="start" symbol={unitCurrencySymbol}>
                  <Input
                    type="number"
                    size="small"
                    className={
                      unitCurrencySymbol &&
                      (typeof unitCurrencySymbol === 'string' && unitCurrencySymbol.length >= 3 ? '!pl-11' : '!pl-6')
                    }
                    {...field}
                  />
                </CurrencyAdornment>
              )}
            />
          </GridCol>
          <GridCol span={1}>
            <Label size="small" className="mb-0.5 text-white" required>
              {tGlobal('price.unitCurrency')}
            </Label>
            <FormField<FormValues, FormPath>
              name={'unitAmount.currency.code'}
              defaultValue={''}
              required
              render={({ field: { value, onChange, ref, ...field } }) => (
                <CurrencyAutocomplete
                  size="small"
                  selected={value}
                  defaultCurrencyCode={defaultCurrencyCode}
                  onChange={onChange}
                  onClear={() => onChange(defaultCurrencyCode)}
                  {...field}
                />
              )}
            />
          </GridCol>

          {/** Interval option */}
          <GridCol span={2}>
            <Label size="small" className="mb-0.5 text-white" required>
              {tGlobal('agreement.invoicingPeriod')}
            </Label>
            <Select
              value={intervalOption}
              size="small"
              onChange={(e) => updateIntervalOption(e.target.value as IntervalOption)}
              error={hasIntervalErrorMessage}
            >
              {INTERVAL_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {tType(`IntervalOption.${option}`)}
                </option>
              ))}
            </Select>
            {hasIntervalErrorMessage && <HelperText error>{intervalErrorMessage}</HelperText>}
          </GridCol>

          {/** Custom interval with type & count */}
          {intervalOption === 'Custom' && (
            <>
              <GridCol span={1}>
                <Label size="small" className="mb-0.5" required>
                  {tGlobal('price.intervalCount')}
                </Label>
                <FormField<FormValues, FormPath>
                  name="interval.count"
                  required
                  defaultValue={''}
                  render={({ field }) => <Input size="small" {...field} />}
                />
              </GridCol>

              <GridCol span={1}>
                <Label size="small" className="mb-0.5" required>
                  {tGlobal('price.intervalType')}
                </Label>
                <FormField<FormValues, FormPath>
                  name="interval.type"
                  required
                  render={({ field }) => (
                    <Select size="small" {...field}>
                      <option />
                      {INTERVAL_TYPE_OPTIONS.map((type) => (
                        <option key={type} value={type}>
                          {tType(`IntervalType.${type}`)}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </GridCol>
            </>
          )}
        </GridRow>

        <div className="flex space-x-2 mt-2">
          <FormSubmitButton
            type="button"
            color="primary"
            size="none"
            onClick={() => {
              handleSubmit(onSubmit)();
            }}
            className="text-xs font-medium px-2 py-1"
          >
            {tCommon('apply')}
          </FormSubmitButton>
          <Button color="primary-dark" size="none" onClick={onCancel} className="text-xs font-medium px-2 py-1">
            {tCommon('cancel')}
          </Button>
        </div>
      </div>
    </FormProvider>
  );
};
